/**
 * Builds the <head> data for a registry route (title, description, canonical,
 * robots, Open Graph). Shared by the client and, later, the prerender.
 */

import { getRouteCopy, type Locale } from "../content";
import { DEFAULT_OG_IMAGE, SITE_URL } from "./config";
import { getRoute, type RouteId } from "./routes";

export interface HeadData {
  title: string;
  description: string;
  /** Absolute URL. Always the route's primary path, never an alias. */
  canonical: string;
  robots: "index" | "noindex";
  ogType: "website" | "article";
  image: string;
}

export function absoluteUrl(path: string): string {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

/**
 * Routes with ":params" have no canonical of their own: pass the current
 * pathname so the canonical is the real URL, never the pattern.
 */
export function buildRouteHead(
  id: RouteId,
  locale: Locale = "pt-BR",
  pathname?: string
): HeadData {
  const route = getRoute(id);
  const copy = getRouteCopy(id, locale);
  let path = route.path;
  if (path.includes(":")) {
    path = pathname
      ? pathname.replace(/\/*$/, path.endsWith("/") ? "/" : "")
      : path.replace(/\/:[^/]+/g, "") || "/";
  }
  return {
    title: copy.title,
    description: copy.description,
    canonical: absoluteUrl(path),
    robots: route.robots,
    ogType: "website",
    image: DEFAULT_OG_IMAGE,
  };
}

export function buildBlogPostHead(post: {
  slug: string;
  title: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
  excerpt?: string | null;
  coverImage?: string | null;
}): HeadData {
  // og:image must be an absolute http(s) URL; anything else (e.g. a data:
  // URI not yet moved to R2) falls back to the default image.
  const cover = post.coverImage ?? "";
  const image = cover.startsWith("/")
    ? absoluteUrl(cover)
    : /^https?:\/\//.test(cover)
      ? cover
      : DEFAULT_OG_IMAGE;
  return {
    title: `${post.seoTitle || post.title} | Codecious Blog`,
    description: post.seoDescription || post.excerpt || "",
    canonical: absoluteUrl(`/blog/${post.slug}/`),
    robots: "index",
    ogType: "article",
    image,
  };
}
